/**
 * The "picture" field (`field-file-input` + `field-file-label`): a server
 * picture, the preview of a chosen file, an upload placeholder and a remove
 * button. Its state comes from `usePicture` (use-picture.ts).
 */
import type { CSSProperties, ReactNode } from 'react';
import { pictureMessage, type PictureController } from './use-picture';
import uploadIcon from '@/assets/images/upload.svg';
import closeIcon from '@/assets/images/close.svg';

export interface PictureFieldProps {
  id: string;
  name: string;
  label: string;
  picture: PictureController;
  /** Signed URL of the picture already stored, when editing. */
  serverUrl: string | null;
  /** Renders the preview (server or chosen picture); default: the plain `.icon` image. */
  renderPreview?: (src: string, testId?: string) => ReactNode;
  /** Extra content inside the label (zoom buttons, for instance). */
  children?: ReactNode;
  labelClassName?: string;
  /** Inline style of the frame (the dark background of a light picture, for instance). */
  labelStyle?: CSSProperties | undefined;
  error?: string | null;
  /** Validation messages and a missing required picture are written under the field. */
  showMessages?: boolean;
  /** A picture must be present to save (dms-picture-upload `isRequired`). */
  required?: boolean;
  submitted?: boolean;
  testId?: string | undefined;
}

export function PictureField({
  id,
  name,
  label,
  picture,
  serverUrl,
  renderPreview,
  children,
  labelClassName = '',
  labelStyle,
  error,
  showMessages = false,
  required = false,
  submitted = false,
  testId,
}: Readonly<PictureFieldProps>) {
  const showServer = !picture.serverHidden && !!serverUrl;
  const message = showMessages
    ? pictureMessage(picture, serverUrl, required, submitted)
    : (error ?? null);
  const expected = picture.expected;
  const subId = (suffix: string) =>
    testId ? `${testId}-${suffix}` : undefined;
  const preview = (src: string) =>
    renderPreview ? (
      renderPreview(src, subId('preview'))
    ) : (
      <img className="icon" src={src} alt="" data-testid={subId('preview')} />
    );
  return (
    <div className="mb-4">
      <span className="field-label" id={`${id}-label`}>
        {label}
        {expected && (
          <span>
            {' '}
            ({expected.width}x{expected.height}px)
          </span>
        )}
        {required && <span aria-hidden="true">*</span>}
      </span>
      <input
        className="field field-file-input"
        name={name}
        id={id}
        type="file"
        accept=".png, .jpg, .jpeg, .gif"
        aria-labelledby={`${id}-label`}
        onChange={(event) => void picture.onChosen(event)}
        data-testid={subId('input')}
      />
      <label
        htmlFor={id}
        className={`field field-file-label ${labelClassName}`}
        style={labelStyle}
      >
        {showServer && renderPreview && (
          // Probe: a custom preview cannot report a missing server picture itself.
          <img
            src={serverUrl}
            alt=""
            hidden
            onError={picture.hideServerPicture}
          />
        )}
        {showServer &&
          (renderPreview ? (
            renderPreview(serverUrl, subId('preview'))
          ) : (
            <img
              className="icon"
              src={serverUrl}
              alt=""
              onError={picture.hideServerPicture}
              data-testid={subId('preview')}
            />
          ))}
        {picture.file && picture.base64 && preview(picture.base64)}
        {!picture.file && !showServer && (
          <img className="upload-icon" src={uploadIcon} alt="" />
        )}
        {(picture.file || showServer) && (
          <button
            type="button"
            className="remove-icon"
            onClick={picture.onRemoved}
            aria-label={`Remove ${label.toLowerCase()}`}
            data-testid={subId('remove-button')}
          >
            <img src={closeIcon} alt="" />
          </button>
        )}
        {children}
      </label>
      {(error !== undefined || showMessages) && (
        <small hidden={!message} data-testid={subId('error')}>
          {message}
        </small>
      )}
    </div>
  );
}
